/**
 * ErrorBoundary Component
 *
 * React Error Boundary that catches JavaScript errors anywhere in the component tree,
 * logs them to the error monitor, and displays a fallback UI.
 *
 * Features:
 * - Catches React component errors
 * - Logs to error monitoring service
 * - Displays user-friendly error UI
 * - Development vs production modes
 * - Reset error state functionality
 * - Error details in development
 *
 * @component
 */

import React, { Component } from 'react';
import errorMonitor from '../services/errorMonitor';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to error monitor
    errorMonitor.trackReactError(error, errorInfo);

    // Store error details in state
    this.setState({
      error,
      errorInfo,
      errorId: `ERR-${Date.now()}`
    });

    // Log to console in development
    if (import.meta.env.DEV) {
      console.error('React Error Boundary caught an error:', error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Development error UI - show details
      if (import.meta.env.DEV) {
        return (
          <div className="min-h-screen bg-gray-900 text-white p-8">
            <div className="max-w-4xl mx-auto">
              <div className="bg-red-500/10 border-2 border-red-500 rounded-lg p-6 mb-6">
                <h1 className="text-3xl font-bold text-red-500 mb-4">
                  ⚠️ React Error Boundary
                </h1>
                <p className="text-lg mb-4">
                  A JavaScript error occurred in a component.
                </p>
                <div className="flex gap-4">
                  <button
                    onClick={this.handleReset}
                    className="px-6 py-2 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition-colors"
                  >
                    Try Again
                  </button>
                  <button
                    onClick={this.handleReload}
                    className="px-6 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg font-semibold transition-colors"
                  >
                    Reload Page
                  </button>
                </div>
              </div>

              {/* Error Details */}
              <div className="bg-gray-800 rounded-lg p-6 mb-6">
                <h2 className="text-xl font-bold mb-4 text-red-400">Error Details</h2>
                <div className="mb-4">
                  <p className="text-sm text-gray-400 mb-1">Error ID:</p>
                  <p className="font-mono text-sm">{this.state.errorId}</p>
                </div>
                <div className="mb-4">
                  <p className="text-sm text-gray-400 mb-1">Message:</p>
                  <p className="font-mono text-sm text-red-400">
                    {this.state.error?.toString()}
                  </p>
                </div>
              </div>

              {/* Stack Trace */}
              {this.state.error?.stack && (
                <div className="bg-gray-800 rounded-lg p-6 mb-6">
                  <h2 className="text-xl font-bold mb-4 text-yellow-400">Stack Trace</h2>
                  <pre className="text-xs overflow-x-auto bg-gray-900 p-4 rounded">
                    {this.state.error.stack}
                  </pre>
                </div>
              )}

              {/* Component Stack */}
              {this.state.errorInfo?.componentStack && (
                <div className="bg-gray-800 rounded-lg p-6">
                  <h2 className="text-xl font-bold mb-4 text-blue-400">Component Stack</h2>
                  <pre className="text-xs overflow-x-auto bg-gray-900 p-4 rounded">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </div>
              )}
            </div>
          </div>
        );
      }

      // Production error UI - user-friendly message
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6">
          <div className="max-w-md w-full text-center">
            <div className="mb-8">
              <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-12 h-12 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h1 className="text-3xl font-display font-bold text-gray-900 mb-4">
                Oops! Something went wrong
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                We're sorry, but something unexpected happened. Please try refreshing the page.
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={this.handleReload}
                className="w-full px-8 py-4 bg-gradient-to-r from-primary-600 to-accent-500 text-white rounded-full font-bold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
              >
                Reload Page
              </button>
              <button
                onClick={this.handleReset}
                className="w-full px-8 py-4 bg-white text-gray-700 border-2 border-gray-300 rounded-full font-bold text-lg hover:border-gray-400 transition-all duration-300"
              >
                Try Again
              </button>
              <a
                href="/"
                className="block w-full px-8 py-4 text-primary-600 font-semibold hover:text-primary-700 transition-colors"
              >
                Go to Homepage
              </a>
            </div>

            {this.state.errorId && (
              <div className="mt-8 p-4 bg-gray-100 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Error Reference:</p>
                <p className="text-sm font-mono text-gray-700">{this.state.errorId}</p>
                <p className="text-xs text-gray-500 mt-2">
                  Please include this reference if you contact support.
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }

    // No error, render children
    return this.props.children;
  }
}

export default ErrorBoundary;

/**
 * Higher-order component to wrap any component with ErrorBoundary
 * @param {React.Component} Component - Component to wrap
 * @param {Object} fallback - Optional fallback UI
 * @returns {React.Component} Wrapped component
 */
export function withErrorBoundary(Component, fallback) {
  return function WithErrorBoundary(props) {
    return (
      <ErrorBoundary fallback={fallback}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };
}
